import { RevenueValue } from "@/components/RevenueEye";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Card, PageHeader } from "@/components/ui-helpers";
import { StaffActivityReport } from "@/components/StaffActivityReport";
import { OnlinePaymentsReport } from "@/components/OnlinePaymentsReport";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#1479C4", "#F5822A", "#5A34B4", "#0EA5A5", "#1E9BE9", "#EAB308", "#7C3AED", "#0891B2"];
const RANGES = [["today", "Today"], ["week", "This Week"], ["month", "This Month"]];

export default function Reports() {
  const { user } = useAuth();
  const canViewAudit = user?.role === "admin" || (user?.permissions || []).includes("view_audit");
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditBusy, setAuditBusy] = useState(false);

  useEffect(() => { api.get(`/dashboard/admin?range=${range}`).then((r) => setData(r.data)).catch(() => {}); }, [range]);
  const [staff, setStaff] = useState([]);
  const [staffFilter, setStaffFilter] = useState("");
  const filterRef = useRef("");
  const loadAudit = (skip = 0, uid = staffFilter) => {
    setAuditBusy(true);
    const qs = `limit=100&skip=${skip}${uid ? `&user_id=${encodeURIComponent(uid)}` : ""}`;
    api.get(`/audit-logs?${qs}`)
      .then((r) => {
        if (filterRef.current !== uid) return;
        setLogs((prev) => (skip === 0 ? r.data.logs : [...prev, ...r.data.logs])); setAuditTotal(r.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setAuditBusy(false));
  };
  const changeStaff = (uid) => { filterRef.current = uid; setStaffFilter(uid); setLogs([]); setAuditTotal(0); loadAudit(0, uid); };
  useEffect(() => {
    if (!canViewAudit) return;
    loadAudit(0, "");
    api.get("/audit-logs/users").then((r) => setStaff(r.data)).catch(() => {});
  }, [canViewAudit]); // eslint-disable-line

  if (!data) return <Loader />;
  const kpis = [
    ...(data.can_see_revenue ? [
      ["Revenue (period)", <RevenueValue value={`$${(data.range_revenue || 0).toFixed(2)}`} testid="reports-revenue-period" screen="reports" />],
      ["Total Collected", <RevenueValue value={`$${(data.revenue || 0).toFixed(2)}`} testid="reports-revenue-total" screen="reports" />],
    ] : []),
    [user?.role === "admin" ? "Outstanding" : "Pending Fees", `$${(data.outstanding_payments || 0).toFixed(2)}`],
    ["New Leads (period)", data.range_new_leads],
    ["New Clients (period)", data.range_new_clients],
    ["New Cases (period)", data.range_new_cases],
  ];

  return (
    <div>
      <PageHeader title="Reports" subtitle="Revenue, pipeline and activity analytics.">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {RANGES.map(([k, l]) => <button key={k} data-testid={`range-${k}`} onClick={() => setRange(k)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${range === k ? "bg-white shadow text-primary" : "text-slate-500"}`}>{l}</button>)}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        {kpis.map(([l, v]) => <Card key={l} className="p-5"><div className="text-xs text-slate-500">{l}</div><div className="font-head font-extrabold text-2xl text-slate-900 mt-1">{v}</div></Card>)}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <Card className="p-5"><h3 className="font-head font-bold text-slate-900 mb-3">Cases by Service</h3>
          <ResponsiveContainer width="100%" height={260}><BarChart data={data.by_service}><XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={70} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="value" fill="#1479C4" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
        </Card>
        <Card className="p-5"><h3 className="font-head font-bold text-slate-900 mb-3">Leads by Status</h3>
          <ResponsiveContainer width="100%" height={260}><PieChart><Pie data={data.leads_by_status} dataKey="value" nameKey="name" outerRadius={95} label={(e) => e.name}>{(data.leads_by_status || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5 mb-5" data-testid="lost-reasons-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-bold text-slate-900">Why Leads Were Lost</h3>
          <span className="text-xs text-slate-400" data-testid="lost-recontact-due">{data.recontact_due || 0} due for re-contact</span>
        </div>
        {(data.lost_reasons || []).length === 0 ? <div className="text-sm text-slate-400 py-8 text-center" data-testid="lost-reasons-empty">No leads marked lost in this period.</div> : (
          <ResponsiveContainer width="100%" height={Math.max(160, data.lost_reasons.length * 42)}>
            <BarChart data={data.lost_reasons} layout="vertical" margin={{ left: 20 }}><XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="value" name="Leads" fill="#C8102E" radius={[0, 6, 6, 0]} /></BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {user?.role === "admin" && <OnlinePaymentsReport />}
      {user?.role === "admin" && <StaffActivityReport />}
      {canViewAudit && (
        <Card className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="font-head font-bold text-slate-900 whitespace-nowrap">Activity Log</h3>
            <div className="flex items-center gap-3">
              <select data-testid="audit-staff-filter" value={staffFilter} onChange={(e) => changeStaff(e.target.value)} className="text-sm border border-slate-200 rounded-xl px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:border-primary">
                <option value="">All staff</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <span className="text-xs text-slate-400 whitespace-nowrap" data-testid="audit-log-count">Showing {logs.length} of {auditTotal}</span>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto"><table className="w-full text-sm min-w-[640px]" data-testid="audit-log-table">
            <thead className="text-slate-400 text-xs uppercase sticky top-0 bg-white"><tr><th className="text-left py-2">When</th><th className="text-left py-2">User</th><th className="text-left py-2">Action</th><th className="text-left py-2">Detail</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((l) => (<tr key={l.id}><td className="py-2 text-slate-500 whitespace-nowrap">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td><td className="py-2 text-slate-700">{l.user_name || "—"}</td><td className="py-2"><span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{l.action}</span></td><td className="py-2 text-slate-500">{l.detail}</td></tr>))}
            </tbody>
          </table></div>
          {logs.length < auditTotal && (
            <button data-testid="audit-load-more" onClick={() => loadAudit(logs.length)} disabled={auditBusy} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:border-slate-300 disabled:opacity-60">{auditBusy ? "Loading…" : `Load more (${auditTotal - logs.length} left)`}</button>
          )}
        </Card>
      )}
    </div>
  );
}
